import { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import { AdminClient, ApiException } from '../../../api/api';

interface CreateTenantDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess: () => void;
}

export default function CreateTenantDialog({ open, setOpen, onSuccess }: CreateTenantDialogProps) {
	const client = useApiClient(AdminClient);
	const toastService = useToastService();

	const [code, setCode] = useState('');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);

	useEffect(() => {
		if (!open) {
			setCode('');
			setName('');
			setDescription('');
			setIsActive(true);
			setIsSubmitting(false);
			setIsMaximized(false);
		}
	}, [open]);

	const handleCreate = async () => {
		if (!code.trim()) {
			toastService.error('Code is required');
			return;
		}

		setIsSubmitting(true);
		try {
			await client.createTenant({
				code: code.trim(),
				name: name.trim() || undefined,
				description: description.trim() || undefined,
				isActive,
			});
			toastService.success(`Tenant "${code.trim()}" created and your Admin role assigned`);
			setOpen(false);
			onSuccess();
		} catch (error) {
			if (error instanceof ApiException && error.status === 424) {
				toastService.error(`Tenant "${code.trim()}" was created, but your Admin role could not be assigned. Contact an administrator.`);
				setOpen(false);
				onSuccess();
				return;
			}

			console.error('Error creating tenant:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create tenant: ${errorMessage}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' && !isSubmitting) {
			event.preventDefault();
			handleCreate();
		}
	};

	return (
		<Dialog open={open} onClose={() => !isSubmitting && setOpen(false)} maxWidth="sm" fullWidth fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<span style={{ flex: 1 }}>Create Tenant</span>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={() => setOpen(false)}
					closeDisabled={isSubmitting}
				/>
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
					You will be assigned the Admin role in the new tenant.
				</Typography>
				<TextField
					autoFocus
					margin="dense"
					label="Code"
					type="text"
					fullWidth
					variant="outlined"
					value={code}
					onChange={(e) => setCode(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					required
					helperText="Unique identifier for the tenant"
				/>
				<TextField
					margin="dense"
					label="Name"
					type="text"
					fullWidth
					variant="outlined"
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					helperText="Display name for the tenant"
				/>
				<TextField
					margin="dense"
					label="Description"
					type="text"
					fullWidth
					variant="outlined"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					multiline
					rows={3}
					helperText="Optional description for the tenant"
				/>
				<FormControlLabel
					control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} disabled={isSubmitting} />}
					label="Active"
					sx={{ mt: 1 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button onClick={handleCreate} variant="contained" disabled={isSubmitting || !code.trim()}>
					{isSubmitting ? 'Creating...' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
